/**
 * Instagram Business Adapter (Story 9.4)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * CRITICAL: Instagram Graph API uses the user's long-lived access token directly
 * (same as Facebook Ads). This is the OPPOSITE of Facebook Pages (Story 9.2)
 * which uses Page Access Tokens. credentials.accessToken IS the correct token.
 *
 * Date format: Unix timestamps (like FB Pages), `until` is NON-INCLUSIVE (add 1 day).
 * Response shape: Nested insights { data: [{ name, values: [{value, end_time}] }] }
 * (same as FB Pages, NOT flat Marketing API rows).
 *
 * v22.0+ enforced: `views` replaces `impressions` at both media and account level.
 * IMC-6: Migrated account-level from `impressions` to `views`. Removed deprecated
 * `profile_views` and `website_clicks` (v21.0, Jan 2025). Added new metrics:
 * accounts_engaged, total_interactions, replies, reposts, profile_links_taps, follows_and_unfollows.
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { META_GRAPH_API_VERSION, META_INTEGRATION_SCOPES } from "./scopes";
import {
  discoverMetaAccounts,
  parseAppUsage,
  type MetaDiscoveryFirstError,
  type MetaSectionStatus,
} from "./discovery";
import { parseMetaGraphError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

const META_GRAPH_BASE = `https://graph.facebook.com/${META_GRAPH_API_VERSION}`;

/** Rate limit threshold — stop making calls if x-app-usage call_count > 80% */
const RATE_LIMIT_THRESHOLD = 80;

/** Maximum data retention for Instagram account insights (90 days) */
const MAX_ACCOUNT_INSIGHTS_DAYS = 90;

/** Maximum pages to follow for media pagination */
const MAX_MEDIA_PAGES = 5;

/** Maximum caption length for post records */
const MAX_CAPTION_LENGTH = 200;

// ============================================================================
// Types (local to adapter, NOT exported)
// ============================================================================

interface IGInsightsResponse {
  data?: Array<{
    name: string;
    period: string;
    values: Array<{
      value: number;
      end_time: string;
    }>;
  }>;
  error?: {
    message: string;
    type: string;
    code: number;
    error_subcode?: number;
  };
}

interface IGAccountInfoResponse {
  id?: string;
  username?: string;
  name?: string;
  profile_picture_url?: string;
  followers_count?: number;
  media_count?: number;
  error?: {
    message: string;
    type: string;
    code: number;
  };
}

interface IGMediaListResponse {
  data?: Array<{
    id: string;
    caption?: string;
    media_type?: string;
    media_url?: string;
    thumbnail_url?: string;
    timestamp?: string;
    like_count?: number;
    comments_count?: number;
  }>;
  paging?: {
    next?: string;
  };
  error?: {
    message: string;
    type: string;
    code: number;
  };
}

interface IGMediaInsightsResponse {
  data?: Array<{
    name: string;
    period: string;
    values: Array<{
      value: number;
    }>;
  }>;
  error?: {
    message: string;
    type: string;
    code: number;
  };
}

// ============================================================================
// Meta Error Response Handler
// ============================================================================

/**
 * Attempts to parse a Meta error from an HTTP error response.
 * If the response body contains a recognized Meta error.code, returns an
 * AdapterResult with the correctly mapped error code.
 * Otherwise returns null to let safeFetch()'s default mapping apply.
 */
async function handleMetaFetchError(
  fetchResult: { success: false; error: { code: string; message: string; retryable: boolean; providerStatus?: number; providerMessage?: string } }
): Promise<AdapterResult<never>> {
  if (fetchResult.error.providerMessage) {
    const metaError = parseMetaGraphError(fetchResult.error.providerMessage);
    if (metaError) {
      return adapterErr({
        code: metaError.code,
        message: sanitizeErrorMessage(metaError.message),
        retryable: metaError.retryable,
        providerStatus: fetchResult.error.providerStatus,
      });
    }
  }

  return adapterErr(fetchResult.error as Parameters<typeof adapterErr>[0]);
}

/**
 * Maps a non-complete catalog-discovery section outcome to an AdapterResult
 * error (Story 31.20 AC #3). Strict full-catalog semantics: a truncated
 * catalog is NEVER adapterOk. `rate_limited` is special-cased retryable;
 * otherwise the section's firstError flows through the Meta error path so a
 * revoked token surfaces as token_expired (Graph error 190) rather than a
 * blanket code. (Private copy — mirrors this adapter's private
 * handleMetaFetchError; facebook-ads exports the shared variant.)
 */
async function mapDiscoverySectionFailure(
  status: Exclude<MetaSectionStatus, "complete">,
  firstError: MetaDiscoveryFirstError | undefined
): Promise<AdapterResult<never>> {
  if (status === "rate_limited") {
    return adapterErr({
      code: "rate_limited",
      message: "Meta account discovery stopped: app-level rate limit threshold reached",
      retryable: true,
    });
  }
  if (firstError) {
    return handleMetaFetchError({ success: false, error: firstError });
  }
  if (status === "aborted") {
    return adapterErr({
      code: "network_error",
      message: "Meta account discovery aborted before the full catalog was fetched",
      retryable: true,
    });
  }
  return adapterErr({
    code: "invalid_response",
    message: `Meta account discovery did not complete the required section (status: ${status})`,
    retryable: false,
  });
}

// ============================================================================
// Helper: Date Range Construction (Unix timestamps, NON-INCLUSIVE until)
// ============================================================================

/**
 * Clamps the start date to max 90 days ago (Instagram account insights retention).
 * Returns unix timestamps (seconds) for the API call.
 * Adds 1 day to end date because Instagram's `until` param is non-inclusive.
 */
function buildInsightsDateRange(start: Date, end: Date): { since: number; until: number; clamped: boolean } {
  const maxRetention = new Date();
  maxRetention.setUTCDate(maxRetention.getUTCDate() - MAX_ACCOUNT_INSIGHTS_DAYS);

  let clamped = false;
  let effectiveStart = start;
  if (start < maxRetention) {
    effectiveStart = maxRetention;
    clamped = true;
  }

  // `until` is non-inclusive — add 1 day
  const untilDate = new Date(end);
  untilDate.setUTCDate(untilDate.getUTCDate() + 1);

  return {
    since: Math.floor(effectiveStart.getTime() / 1000),
    until: Math.floor(untilDate.getTime() / 1000),
    clamped,
  };
}

// ============================================================================
// Helper: Parse Insights Response to Daily Records
// ============================================================================

/**
 * Parses Instagram Insights API response into a map of date → metric values.
 * Same structure as Facebook Pages: { data: [{ name, values: [{value, end_time}] }] }
 */
function parseInsightsResponse(
  data: IGInsightsResponse["data"]
): Map<string, Record<string, number>> {
  const dailyMap = new Map<string, Record<string, number>>();

  if (!data) return dailyMap;

  for (const metric of data) {
    for (const val of metric.values) {
      const dateStr = val.end_time.split("T")[0];
      if (!dateStr) continue;

      if (!dailyMap.has(dateStr)) {
        dailyMap.set(dateStr, {});
      }
      const record = dailyMap.get(dateStr)!;
      record[metric.name] = val.value;
    }
  }

  return dailyMap;
}

// ============================================================================
// Helper: Day of Week
// ============================================================================

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function getDayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return DAYS_OF_WEEK[d.getUTCDay()] ?? "Unknown";
}

// ============================================================================
// Helper: Truncate Caption
// ============================================================================

function truncateCaption(caption: string | undefined): string {
  if (!caption) return "";
  if (caption.length <= MAX_CAPTION_LENGTH) return caption;
  return caption.slice(0, MAX_CAPTION_LENGTH - 3) + "...";
}

// ============================================================================
// Metric Definitions (Subtask 1.2)
// ============================================================================

export const IG_BUSINESS_METRICS: MetricDefinition[] = [
  // Growth
  { key: "followers_count", label: "Followers", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "followers_change", label: "Follower Change", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "follows_and_unfollows", label: "Follows & Unfollows", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  // Delivery
  { key: "reach", label: "Reach", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "views", label: "Views", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "accounts_engaged", label: "Accounts Engaged", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  // Engagement
  { key: "total_interactions", label: "Total Interactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "likes", label: "Likes", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "comments", label: "Comments", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "saves", label: "Saves", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "shares", label: "Shares", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "replies", label: "Replies", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "reposts", label: "Reposts", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "profile_links_taps", label: "Profile Link Taps", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  // Performance
  { key: "engagement_rate", label: "Engagement Rate", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "total_interactions", denominator: "reach" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  // Video / Reels
  { key: "reel_plays", label: "Reel Plays", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "reel_reach", label: "Reel Reach", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "reel_avg_watch_time", label: "Reel Avg Watch Time", type: "number", category: "Video", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "reel_plays" }, compatibleWidgets: ["kpi", "line-chart", "data-table"] },
  { key: "reel_total_watch_time", label: "Reel Total Watch Time", type: "number", category: "Video", dataType: "Duration", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "reel_skip_rate", label: "Reel Skip Rate", type: "percentage", category: "Video", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "reel_plays" }, compatibleWidgets: ["kpi", "gauge", "data-table"] },
  { key: "reel_reposts", label: "Reel Reposts", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "crossposted_views", label: "Crossposted Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "facebook_views", label: "Facebook Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  // Stories
  { key: "story_reach", label: "Story Reach", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "story_replies", label: "Story Replies", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "story_exits", label: "Story Exits", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "story_taps_forward", label: "Story Taps Forward", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "story_taps_back", label: "Story Taps Back", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "story_navigation", label: "Story Navigation", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "story_follows", label: "Story Follows", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  // Feed Post Activity
  { key: "post_follows", label: "Post Follows", type: "number", category: "Growth", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "post_profile_visits", label: "Post Profile Visits", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "post_profile_activity", label: "Post Profile Activity", type: "number", category: "Content", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  // Demographics
  { key: "follower_demographics_age", label: "Follower Demographics (Age)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "bar-chart", "pie-chart"] },
  { key: "follower_demographics_gender", label: "Follower Demographics (Gender)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "pie-chart"] },
  { key: "follower_demographics_city", label: "Follower Demographics (City)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "data-table"] },
  { key: "follower_demographics_country", label: "Follower Demographics (Country)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "bar-chart", "pie-chart"] },
  // Online Followers
  { key: "online_followers", label: "Online Followers", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "data-table"] },
  // IMC-21: Engaged Audience Demographics
  { key: "engaged_audience_demographics_age", label: "Engaged Audience (Age)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "bar-chart", "pie-chart"] },
  { key: "engaged_audience_demographics_gender", label: "Engaged Audience (Gender)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "pie-chart"] },
  { key: "engaged_audience_demographics_city", label: "Engaged Audience (City)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "data-table"] },
  { key: "engaged_audience_demographics_country", label: "Engaged Audience (Country)", type: "number", category: "Audience", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["demographic", "bar-chart", "pie-chart"] },
  // IMC-21: Post Total Interactions (derived)
  { key: "post_total_interactions", label: "Post Total Interactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table", "top-posts"] },
  // IMC-21: Reel Interaction Metrics (media-level)
  { key: "reel_likes", label: "Reel Likes", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table", "top-posts"] },
  { key: "reel_comments", label: "Reel Comments", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table", "top-posts"] },
  { key: "reel_shares", label: "Reel Shares", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table", "top-posts"] },
  { key: "reel_saves", label: "Reel Saves", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table", "top-posts"] },
  // IMC-21: Story Metrics (media-level)
  { key: "story_views", label: "Story Views", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "story_shares", label: "Story Shares", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "story_profile_visits", label: "Story Profile Visits", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "story_profile_activity", label: "Story Profile Activity", type: "number", category: "Stories", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
];

// ============================================================================
// Dimension Definitions (Subtask 1.3)
// ============================================================================

const IG_BUSINESS_DIMENSIONS: DimensionDefinition[] = [
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01", "2026-03-02"] },
  { key: "day_of_week", label: "Day of Week", type: "Time", exampleValues: ["Monday", "Tuesday"] },
  { key: "media_type", label: "Media Type", type: "Content", exampleValues: ["IMAGE", "VIDEO", "CAROUSEL_ALBUM", "REEL", "STORY"] },
  { key: "post_type", label: "Post Type", type: "Content", exampleValues: ["post", "reel", "story"] },
  { key: "media_product_type", label: "Media Product Type", type: "Content", exampleValues: ["AD", "FEED", "REELS", "STORY"] },
  { key: "follow_type", label: "Follow Type", type: "Content", exampleValues: ["FOLLOWER", "NON_FOLLOWER"] },
  { key: "contact_button_type", label: "Contact Button Type", type: "Content", exampleValues: ["BOOK_NOW", "CALL", "EMAIL", "TEXT", "DIRECTION"] },
  { key: "follower_type", label: "Follower Type", type: "Content", exampleValues: ["follower", "non_follower"] },
];

// ============================================================================
// Account-Level Insights Metrics
// ============================================================================

/**
 * Metrics fetched via /{ig-user-id}/insights?metric=...&period=day
 *
 * IMC-6 migration:
 * - `impressions` → `views` (deprecated v22.0, hard-removed Apr 21, 2025)
 * - `profile_views` removed (deprecated v21.0, Jan 8, 2025 — use `profile_links_taps`)
 * - `website_clicks` removed (deprecated v21.0, Jan 8, 2025 — use `profile_links_taps`)
 * - Added: `accounts_engaged`, `total_interactions`, `replies`, `reposts`, `profile_links_taps`, `follows_and_unfollows`
 */
const ACCOUNT_INSIGHTS_METRICS = [
  "reach",
  "views",
  "follower_count",
  "accounts_engaged",
  "total_interactions",
  "replies",
  "reposts",
  "profile_links_taps",
  "follows_and_unfollows",
];

/** Media-level insight metrics (v22.0+ safe — NO `impressions`, `plays`, or `clips_replays_count`) */
const MEDIA_INSIGHTS_METRICS = [
  "reach",
  "saved",
  "shares",
  "total_interactions",
  "views",
];

/** Additional metrics for Reels only */
const REEL_INSIGHTS_METRICS = [
  ...MEDIA_INSIGHTS_METRICS,
  "ig_reels_avg_watch_time",
  "ig_reels_video_view_total_time",
  "reels_skip_rate",
  "reposts",
  "likes",
  "comments",
  "crossposted_views",
  "facebook_views",
];

/** Additional metrics for Stories only (24hr window) — IMC-21: added views, total_interactions */
const STORY_INSIGHTS_METRICS = [
  "reach",
  "views",
  "total_interactions",
  "exits",
  "taps_forward",
  "taps_back",
  "navigation",
  "replies",
  "shares",
  "follows",
  "profile_visits",
  "profile_activity",
];

/** Feed post metrics (IMAGE, VIDEO, CAROUSEL_ALBUM) — includes profile activity */
const FEED_POST_INSIGHTS_METRICS = [
  ...MEDIA_INSIGHTS_METRICS,
  "likes",
  "comments",
  "follows",
  "profile_visits",
  "profile_activity",
];

// ============================================================================
// Instagram Business Adapter (Subtask 1.1)
// ============================================================================

export const instagramBusinessAdapter: IntegrationAdapter = {
  platform: "instagram-business",

  rateLimits: {
    requestsPerMinute: 3,
    requestsPerDay: 4800, // 200 calls/hr shared with other Meta platforms
  },

  // --------------------------------------------------------------------------
  // fetchMetrics (Subtask 1.4)
  // --------------------------------------------------------------------------
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const igUserId = ctx.accountId;
    if (!igUserId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Instagram Business account selected. Please select an account first.",
        retryable: false,
      });
    }

    // Use user's long-lived token directly (same as Facebook Ads, NOT Page Token)
    const token = credentials.accessToken;
    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    // Clamp date range to 90 days (account insights retention)
    const { since, until, clamped } = buildInsightsDateRange(dateRange.start, dateRange.end);

    if (clamped) {
      console.log(JSON.stringify({
        event: "ig_business_date_clamped",
        igUserId,
        tenantId: ctx.tenantId,
        originalStart: dateRange.start.toISOString(),
        clampedTo: new Date(since * 1000).toISOString(),
      }));
    }

    let rateLimitWarning = false;

    // --- Fetch account-level insights (daily time-series) ---
    const insightsUrl = `${META_GRAPH_BASE}/${igUserId}/insights?metric=${ACCOUNT_INSIGHTS_METRICS.join(",")}&period=day&since=${since}&until=${until}`;

    const insightsResult = await safeFetch(insightsUrl, authHeaders, 25_000);

    if (!insightsResult.success) {
      return handleMetaFetchError(insightsResult);
    }

    const insightsResponse = insightsResult.data;
    const appUsage = parseAppUsage(insightsResponse);
    if (appUsage > RATE_LIMIT_THRESHOLD) {
      rateLimitWarning = true;
      console.log(JSON.stringify({
        event: "meta_rate_limit_approaching",
        callCount: appUsage,
        igUserId,
        tenantId: ctx.tenantId,
      }));
    }

    let insightsData: IGInsightsResponse;
    try {
      insightsData = (await insightsResponse.json()) as IGInsightsResponse;
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(
          `Failed to parse Instagram Insights response: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }

    // Check for API-level error in the JSON body
    if (insightsData.error) {
      const metaError = parseMetaGraphError(JSON.stringify(insightsData));
      if (metaError) {
        return adapterErr({
          code: metaError.code,
          message: sanitizeErrorMessage(metaError.message),
          retryable: metaError.retryable,
        });
      }
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(insightsData.error.message),
        retryable: false,
      });
    }

    // Parse insights into daily records
    const dailyMap = parseInsightsResponse(insightsData.data);

    // --- Fetch current follower count (point-in-time) ---
    let currentFollowers = 0;
    if (!rateLimitWarning) {
      const accountInfoUrl = `${META_GRAPH_BASE}/${igUserId}?fields=followers_count,media_count`;
      const infoResult = await safeFetch(accountInfoUrl, authHeaders, 5_000);

      if (infoResult.success) {
        const infoAppUsage = parseAppUsage(infoResult.data);
        if (infoAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }
        try {
          const infoBody = (await infoResult.data.json()) as IGAccountInfoResponse;
          currentFollowers = infoBody.followers_count ?? 0;
        } catch {
          // Non-fatal — point-in-time follower count is supplementary
        }
      }
    }

    // --- Fetch media list for Top Posts widget ---
    const mediaRecords: Record<string, unknown>[] = [];
    if (!rateLimitWarning) {
      const mediaUrl = `${META_GRAPH_BASE}/${igUserId}/media?fields=id,caption,media_type,media_url,thumbnail_url,timestamp,like_count,comments_count&limit=50`;

      let nextMediaUrl: string | null = mediaUrl;
      let mediaPageCount = 0;

      while (nextMediaUrl && mediaPageCount < MAX_MEDIA_PAGES && !rateLimitWarning) {
        const mediaResult = await safeFetch(nextMediaUrl, authHeaders, 25_000);
        if (!mediaResult.success) break;

        const mediaAppUsage = parseAppUsage(mediaResult.data);
        if (mediaAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
          console.log(JSON.stringify({
            event: "meta_rate_limit_approaching",
            callCount: mediaAppUsage,
            igUserId,
            tenantId: ctx.tenantId,
            context: "media_list",
          }));
          break;
        }

        let mediaData: IGMediaListResponse;
        try {
          mediaData = (await mediaResult.data.json()) as IGMediaListResponse;
        } catch {
          break;
        }

        if (mediaData.error) break;

        for (const media of mediaData.data ?? []) {
          // Determine which metrics to request based on media type
          const mediaType = media.media_type ?? "IMAGE";
          let metricsToRequest: string[];
          if (mediaType === "REEL") {
            metricsToRequest = REEL_INSIGHTS_METRICS;
          } else if (mediaType === "STORY") {
            // NOTE: /{ig-user-id}/media does NOT return Stories — they require /{ig-user-id}/stories.
            // This branch exists for future Story polling support but is currently unreachable via the media list endpoint.
            metricsToRequest = STORY_INSIGHTS_METRICS;
          } else {
            metricsToRequest = FEED_POST_INSIGHTS_METRICS;
          }

          // Fetch per-media insights
          const insightsValues: Record<string, number> = {};

          if (!rateLimitWarning) {
            const mediaInsightsUrl = `${META_GRAPH_BASE}/${media.id}/insights?metric=${metricsToRequest.join(",")}`;
            const mediaInsightsResult = await safeFetch(mediaInsightsUrl, authHeaders, 10_000);

            if (mediaInsightsResult.success) {
              const miAppUsage = parseAppUsage(mediaInsightsResult.data);
              if (miAppUsage > RATE_LIMIT_THRESHOLD) {
                rateLimitWarning = true;
              }

              try {
                const miData = (await mediaInsightsResult.data.json()) as IGMediaInsightsResponse;
                if (miData.data) {
                  for (const metric of miData.data) {
                    insightsValues[metric.name] = metric.values[0]?.value ?? 0;
                  }
                }
              } catch {
                // Non-fatal — skip this media's insights
              }
            }
          }

          const mediaReach = insightsValues.reach ?? 0;
          const mediaSaves = insightsValues.saved ?? 0;
          const mediaShares = insightsValues.shares ?? 0;
          const mediaViews = insightsValues.views ?? 0;
          const likes = insightsValues.likes ?? media.like_count ?? 0;
          const comments = insightsValues.comments ?? media.comments_count ?? 0;
          const totalEngagement = likes + comments + mediaSaves + mediaShares;
          const engagementRate = mediaReach > 0 ? Math.round((totalEngagement / mediaReach) * 100 * 100) / 100 : 0;

          const postRecord: Record<string, unknown> = {
            _reportType: "posts",
            id: media.id,
            caption: truncateCaption(media.caption),
            media_type: mediaType,
            media_url: media.media_url ?? "",
            thumbnail_url: media.thumbnail_url ?? "",
            timestamp: media.timestamp ?? "",
            likes,
            comments,
            saves: mediaSaves,
            shares: mediaShares,
            reach: mediaReach,
            views: mediaViews,
            total_interactions: insightsValues.total_interactions ?? totalEngagement,
            engagement_rate: engagementRate,
            // IMC-21: Derived post total interactions (likes + comments + shares + saves)
            post_total_interactions: likes + comments + mediaSaves + mediaShares,
          };

          // Reel-specific metrics (Task 3) — IMC-21: added reel_likes/comments/shares/saves
          if (mediaType === "REEL") {
            // reel_avg_watch_time: convert from ms to seconds
            postRecord.reel_avg_watch_time = insightsValues.ig_reels_avg_watch_time
              ? Math.round((insightsValues.ig_reels_avg_watch_time / 1000) * 100) / 100
              : 0;
            postRecord.reel_total_watch_time = insightsValues.ig_reels_video_view_total_time ?? 0;
            postRecord.reel_skip_rate = insightsValues.reels_skip_rate ?? 0;
            postRecord.reel_reposts = insightsValues.reposts ?? 0;
            postRecord.crossposted_views = insightsValues.crossposted_views ?? 0;
            postRecord.facebook_views = insightsValues.facebook_views ?? 0;
            // IMC-21: Named reel interaction metrics for per-reel reporting
            postRecord.reel_likes = likes;
            postRecord.reel_comments = comments;
            postRecord.reel_shares = mediaShares;
            postRecord.reel_saves = mediaSaves;
          }

          // Story-specific metrics (Task 4) — prefixed to match MetricDefinition keys
          // NOTE: This branch is only reachable if the media list endpoint ever returns STORY items.
          // Currently stories are fetched separately via /{ig-user-id}/stories (IMC-21).
          if (mediaType === "STORY") {
            postRecord.story_views = insightsValues.views ?? 0;
            postRecord.story_shares = insightsValues.shares ?? 0;
            postRecord.story_exits = insightsValues.exits ?? 0;
            postRecord.story_taps_forward = insightsValues.taps_forward ?? 0;
            postRecord.story_taps_back = insightsValues.taps_back ?? 0;
            postRecord.story_navigation = insightsValues.navigation ?? 0;
            postRecord.story_follows = insightsValues.follows ?? 0;
            postRecord.story_profile_visits = insightsValues.profile_visits ?? 0;
            postRecord.story_profile_activity = insightsValues.profile_activity ?? 0;
            postRecord.replies = insightsValues.replies ?? 0;
            postRecord.post_profile_visits = insightsValues.profile_visits ?? 0;
            postRecord.post_profile_activity = insightsValues.profile_activity ?? 0;
          }

          // Feed post activity metrics (Task 5) — prefixed to match MetricDefinition keys
          if (mediaType !== "STORY") {
            postRecord.post_follows = insightsValues.follows ?? 0;
            postRecord.post_profile_visits = insightsValues.profile_visits ?? 0;
            postRecord.post_profile_activity = insightsValues.profile_activity ?? 0;
          }

          mediaRecords.push(postRecord);
        }

        nextMediaUrl = mediaData.paging?.next ?? null;
        mediaPageCount++;
      }
    }

    // --- IMC-21: Fetch active stories via /{ig-user-id}/stories ---
    // Stories expire after 24h — only current/active stories are returned.
    // This is separate from /{ig-user-id}/media which does NOT include stories.
    if (!rateLimitWarning) {
      const storiesUrl = `${META_GRAPH_BASE}/${igUserId}/stories?fields=id,caption,media_type,media_url,thumbnail_url,timestamp`;
      const storiesResult = await safeFetch(storiesUrl, authHeaders, 15_000);

      if (storiesResult.success) {
        const storiesAppUsage = parseAppUsage(storiesResult.data);
        if (storiesAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }

        try {
          const storiesData = (await storiesResult.data.json()) as IGMediaListResponse;
          if (storiesData.data && !storiesData.error) {
            for (const story of storiesData.data) {
              if (rateLimitWarning) break;

              // Fetch per-story insights
              const storyInsightsUrl = `${META_GRAPH_BASE}/${story.id}/insights?metric=${STORY_INSIGHTS_METRICS.join(",")}`;
              const storyInsightsResult = await safeFetch(storyInsightsUrl, authHeaders, 10_000);

              const storyInsights: Record<string, number> = {};
              if (storyInsightsResult.success) {
                const siAppUsage = parseAppUsage(storyInsightsResult.data);
                if (siAppUsage > RATE_LIMIT_THRESHOLD) {
                  rateLimitWarning = true;
                }
                try {
                  const siData = (await storyInsightsResult.data.json()) as IGMediaInsightsResponse;
                  if (siData.data) {
                    for (const metric of siData.data) {
                      storyInsights[metric.name] = metric.values[0]?.value ?? 0;
                    }
                  }
                } catch {
                  // Non-fatal — skip this story's insights
                }
              }

              const storyLikes = story.like_count ?? 0;
              const storyComments = story.comments_count ?? 0;
              const storyReach = storyInsights.reach ?? 0;
              const storyViews = storyInsights.views ?? 0;
              const storyShares = storyInsights.shares ?? 0;

              mediaRecords.push({
                _reportType: "posts",
                id: story.id,
                caption: truncateCaption(story.caption),
                media_type: "STORY",
                media_url: story.media_url ?? "",
                thumbnail_url: story.thumbnail_url ?? "",
                timestamp: story.timestamp ?? "",
                likes: storyLikes,
                comments: storyComments,
                saves: 0, // Stories don't have saves
                shares: storyShares,
                reach: storyReach,
                views: storyViews,
                total_interactions: storyInsights.total_interactions ?? (storyLikes + storyComments + storyShares),
                engagement_rate: storyReach > 0 ? Math.round(((storyLikes + storyComments + storyShares) / storyReach) * 100 * 100) / 100 : 0,
                post_total_interactions: storyLikes + storyComments + storyShares,
                // Story-specific metrics (IMC-21 Task 3)
                story_views: storyViews,
                story_shares: storyShares,
                story_exits: storyInsights.exits ?? 0,
                story_taps_forward: storyInsights.taps_forward ?? 0,
                story_taps_back: storyInsights.taps_back ?? 0,
                story_navigation: storyInsights.navigation ?? 0,
                story_follows: storyInsights.follows ?? 0,
                story_profile_visits: storyInsights.profile_visits ?? 0,
                story_profile_activity: storyInsights.profile_activity ?? 0,
                replies: storyInsights.replies ?? 0,
                post_profile_visits: storyInsights.profile_visits ?? 0,
                post_profile_activity: storyInsights.profile_activity ?? 0,
              });
            }
          }
        } catch {
          // Non-fatal — stories are ephemeral
        }
      }
    }

    // --- Fetch demographics (Task 6) — requires 100+ followers ---
    if (!rateLimitWarning && currentFollowers >= 100) {
      // Follower demographics
      const followerDemoUrl = `${META_GRAPH_BASE}/${igUserId}/insights?metric=follower_demographics&period=lifetime&timeframe=last_30_days&metric_type=total_value&breakdown=age,gender,city,country`;
      const followerDemoResult = await safeFetch(followerDemoUrl, authHeaders, 15_000);

      if (followerDemoResult.success) {
        const demoAppUsage = parseAppUsage(followerDemoResult.data);
        if (demoAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }

        try {
          const demoData = (await followerDemoResult.data.json()) as IGInsightsResponse;
          if (demoData.data && !demoData.error) {
            for (const metric of demoData.data) {
              // total_value responses have a different shape — value is an object with breakdown keys
              const totalValue = (metric.values[0] as unknown as { value: Record<string, number> })?.value;
              if (totalValue && typeof totalValue === "object") {
                mediaRecords.push({
                  _reportType: "demographics",
                  demographic_type: "follower",
                  breakdown: metric.name.replace("follower_demographics.", ""),
                  data: totalValue,
                });
              }
            }
          }
        } catch {
          // Non-fatal — demographics are supplementary
        }
      }

      // Engaged audience demographics — IMC-21: generates both "demographics" and "engagedAudienceDemographics" reportType records
      if (!rateLimitWarning) {
        const engagedDemoUrl = `${META_GRAPH_BASE}/${igUserId}/insights?metric=engaged_audience_demographics&period=lifetime&timeframe=last_30_days&metric_type=total_value&breakdown=age,gender,city,country`;
        const engagedDemoResult = await safeFetch(engagedDemoUrl, authHeaders, 15_000);

        if (engagedDemoResult.success) {
          const edAppUsage = parseAppUsage(engagedDemoResult.data);
          if (edAppUsage > RATE_LIMIT_THRESHOLD) {
            rateLimitWarning = true;
          }

          try {
            const edData = (await engagedDemoResult.data.json()) as IGInsightsResponse;
            if (edData.data && !edData.error) {
              for (const metric of edData.data) {
                const totalValue = (metric.values[0] as unknown as { value: Record<string, number> })?.value;
                if (totalValue && typeof totalValue === "object") {
                  const breakdown = metric.name.replace("engaged_audience_demographics.", "");
                  // Legacy format (backward-compat)
                  mediaRecords.push({
                    _reportType: "demographics",
                    demographic_type: "engaged_audience",
                    breakdown,
                    data: totalValue,
                  });
                  // IMC-21 AC #2: Dedicated engagedAudienceDemographics reportType
                  mediaRecords.push({
                    _reportType: "engagedAudienceDemographics",
                    breakdown,
                    data: totalValue,
                  });
                }
              }
            }
          } catch {
            // Non-fatal
          }
        }
      }
    }

    // --- Fetch online_followers (Task 7) — requires 100+ followers, last 30 days max ---
    if (!rateLimitWarning && currentFollowers >= 100) {
      // online_followers only supports last 30 days — clamp since if needed
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setUTCDate(thirtyDaysAgo.getUTCDate() - 30);
      const onlineSince = Math.max(since, Math.floor(thirtyDaysAgo.getTime() / 1000));
      const onlineFollowersUrl = `${META_GRAPH_BASE}/${igUserId}/insights?metric=online_followers&period=day&since=${onlineSince}&until=${until}`;
      const onlineResult = await safeFetch(onlineFollowersUrl, authHeaders, 15_000);

      if (onlineResult.success) {
        const ofAppUsage = parseAppUsage(onlineResult.data);
        if (ofAppUsage > RATE_LIMIT_THRESHOLD) {
          rateLimitWarning = true;
        }

        try {
          const ofData = (await onlineResult.data.json()) as IGInsightsResponse;
          if (ofData.data && !ofData.error) {
            for (const metric of ofData.data) {
              for (const val of metric.values) {
                // online_followers returns hourly activity map: { "0": count, "1": count, ..., "23": count }
                const dateStr = val.end_time.split("T")[0];
                if (dateStr) {
                  mediaRecords.push({
                    _reportType: "onlineFollowers",
                    date: dateStr,
                    hourly_activity: val.value,
                  });
                }
              }
            }
          }
        } catch {
          // Non-fatal
        }
      }
    }

    // --- Aggregate media engagement per day for daily summary records ---
    const mediaByDate = new Map<string, {
      likes: number; comments: number; saves: number; shares: number;
      reelPlays: number; reelReach: number;
      storyReach: number; storyReplies: number; storyExits: number;
      storyViews: number; storyShares: number;
      storyTapsForward: number; storyTapsBack: number; storyNavigation: number;
      storyFollows: number; storyProfileVisits: number; storyProfileActivity: number;
    }>();

    for (const post of mediaRecords) {
      const postTimestamp = post.timestamp as string;
      if (!postTimestamp) continue;
      const postDate = postTimestamp.split("T")[0];
      if (!postDate) continue;

      if (!mediaByDate.has(postDate)) {
        mediaByDate.set(postDate, {
          likes: 0, comments: 0, saves: 0, shares: 0,
          reelPlays: 0, reelReach: 0,
          storyReach: 0, storyReplies: 0, storyExits: 0,
          storyViews: 0, storyShares: 0,
          storyTapsForward: 0, storyTapsBack: 0, storyNavigation: 0,
          storyFollows: 0, storyProfileVisits: 0, storyProfileActivity: 0,
        });
      }
      const dayAgg = mediaByDate.get(postDate)!;
      dayAgg.likes += ((post.likes as number | undefined) ?? 0);
      dayAgg.comments += ((post.comments as number | undefined) ?? 0);
      dayAgg.saves += ((post.saves as number | undefined) ?? 0);
      dayAgg.shares += ((post.shares as number | undefined) ?? 0);

      const mediaType = post.media_type as string;
      if (mediaType === "REEL" || mediaType === "VIDEO") {
        dayAgg.reelPlays += ((post.views as number | undefined) ?? 0);
        dayAgg.reelReach += ((post.reach as number | undefined) ?? 0);
      }
      if (mediaType === "STORY") {
        dayAgg.storyReach += ((post.reach as number | undefined) ?? 0);
        dayAgg.storyReplies += ((post.replies as number | undefined) ?? 0);
        dayAgg.storyExits += ((post.story_exits as number | undefined) ?? 0);
        dayAgg.storyViews += ((post.story_views as number | undefined) ?? 0);
        dayAgg.storyShares += ((post.story_shares as number | undefined) ?? 0);
        dayAgg.storyTapsForward += ((post.story_taps_forward as number | undefined) ?? 0);
        dayAgg.storyTapsBack += ((post.story_taps_back as number | undefined) ?? 0);
        dayAgg.storyNavigation += ((post.story_navigation as number | undefined) ?? 0);
        dayAgg.storyFollows += ((post.story_follows as number | undefined) ?? 0);
        dayAgg.storyProfileVisits += ((post.story_profile_visits as number | undefined) ?? 0);
        dayAgg.storyProfileActivity += ((post.story_profile_activity as number | undefined) ?? 0);
      }
    }

    // --- Build dailySummary records ---
    const records: Record<string, unknown>[] = [];

    for (const [dateStr, metrics] of dailyMap) {
      const reach = metrics.reach ?? 0;
      const mediaAgg = mediaByDate.get(dateStr);
      const likes = mediaAgg?.likes ?? 0;
      const comments = mediaAgg?.comments ?? 0;
      const saves = mediaAgg?.saves ?? 0;
      const shares = mediaAgg?.shares ?? 0;
      const totalEngagement = likes + comments + saves + shares;
      const engagementRate = reach > 0 ? Math.round((totalEngagement / reach) * 100 * 100) / 100 : 0;

      // follower_count from insights is the daily follower count
      // Use currentFollowers as fallback
      const followersCount = metrics.follower_count ?? currentFollowers;

      records.push({
        _reportType: "dailySummary",
        date: dateStr,
        day_of_week: getDayOfWeek(dateStr),
        reach,
        views: metrics.views ?? 0,
        followers_count: followersCount,
        followers_change: 0, // Calculated as delta between consecutive days in post-processing
        accounts_engaged: metrics.accounts_engaged ?? 0,
        total_interactions: metrics.total_interactions ?? 0,
        likes,
        comments,
        saves,
        shares,
        replies: metrics.replies ?? 0,
        reposts: metrics.reposts ?? 0,
        profile_links_taps: metrics.profile_links_taps ?? 0,
        follows_and_unfollows: metrics.follows_and_unfollows ?? 0,
        engagement_rate: engagementRate,
        reel_plays: mediaAgg?.reelPlays ?? 0,
        reel_reach: mediaAgg?.reelReach ?? 0,
        story_reach: mediaAgg?.storyReach ?? 0,
        story_replies: mediaAgg?.storyReplies ?? 0,
        story_exits: mediaAgg?.storyExits ?? 0,
        // IMC-21: New story aggregates in daily summary
        story_views: mediaAgg?.storyViews ?? 0,
        story_shares: mediaAgg?.storyShares ?? 0,
        story_taps_forward: mediaAgg?.storyTapsForward ?? 0,
        story_taps_back: mediaAgg?.storyTapsBack ?? 0,
        story_navigation: mediaAgg?.storyNavigation ?? 0,
        story_follows: mediaAgg?.storyFollows ?? 0,
        story_profile_visits: mediaAgg?.storyProfileVisits ?? 0,
        story_profile_activity: mediaAgg?.storyProfileActivity ?? 0,
      });
    }

    // Sort daily records by date for followers_change calculation
    records.sort((a, b) => String(a.date).localeCompare(String(b.date)));

    // Calculate followers_change as delta between consecutive days
    for (let i = 1; i < records.length; i++) {
      const current = records[i]!;
      const previous = records[i - 1]!;
      if (current._reportType === "dailySummary" && previous._reportType === "dailySummary") {
        const currentFollowersCount = current.followers_count as number;
        const previousFollowersCount = previous.followers_count as number;
        current.followers_change = currentFollowersCount - previousFollowersCount;
      }
    }

    // Add media records after daily summary
    records.push(...mediaRecords);

    return adapterOk({
      platform: "instagram-business",
      accountId: igUserId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  // --------------------------------------------------------------------------
  // validateConnection (Subtask 1.5)
  // --------------------------------------------------------------------------
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const igUserId = ctx.accountId;
    if (!igUserId) {
      return adapterErr({
        code: "account_not_found",
        message: "No Instagram Business Account ID provided for connection validation.",
        retryable: false,
      });
    }

    const url = `${META_GRAPH_BASE}/${igUserId}?fields=id,username,name,profile_picture_url,followers_count`;
    const result = await safeFetch(
      url,
      { headers: { Authorization: `Bearer ${credentials.accessToken}` } },
      5_000
    );

    if (!result.success) {
      return handleMetaFetchError(result);
    }

    try {
      const body = (await result.data.json()) as IGAccountInfoResponse;
      if (!body.id) {
        return adapterErr({
          code: "account_not_found",
          message: "Invalid Instagram Business Account — no ID returned",
          retryable: false,
        });
      }
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "Failed to parse Instagram account validation response",
        retryable: false,
      });
    }

    return adapterOk(true);
  },

  // --------------------------------------------------------------------------
  // discoverAccounts (Subtask 1.6)
  // --------------------------------------------------------------------------
  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Story 31.20: the IG walk needs the pages list as its parent set, so
    // request pages + instagram (AC #1) — page rows are fetched but NOT
    // emitted by this adapter. BOTH sections must be complete (AC #3): a
    // silently missing IG account is an allowlist gap (fail-closed).
    const discoveryResult = await discoverMetaAccounts({
      accessToken: credentials.accessToken,
      grantedScopes: credentials.scopes,
      sections: { pages: true, adAccounts: false, instagram: true },
    });

    const { pages: pagesStatus, instagram: instagramStatus } = discoveryResult.sectionStatus;
    if (pagesStatus !== "complete") {
      return mapDiscoverySectionFailure(pagesStatus, discoveryResult.firstError);
    }
    if (instagramStatus !== "complete") {
      return mapDiscoverySectionFailure(instagramStatus, discoveryResult.firstError);
    }

    // Filter for Instagram accounts only — never emit page/ads rows (AC #5).
    // Defensive dedupe by id, first occurrence wins (ADR-0004 §Anti-Patterns 4).
    const accountsById = new Map<string, AccountInfo>();
    for (const account of discoveryResult.instagramAccounts) {
      if (accountsById.has(account.id)) continue;
      accountsById.set(account.id, {
        id: account.id,
        name: account.linkedPageName,
        platform: "instagram-business" as const,
        metadata: { linkedPageId: account.linkedPageId, linkedPageName: account.linkedPageName },
        // Fresh spread per row — display/consent metadata only (Story 31-4)
        scopes: [...META_INTEGRATION_SCOPES["instagram-business"].scopes],
      });
    }

    return adapterOk([...accountsById.values()]);
  },

  // --------------------------------------------------------------------------
  // getAvailableMetrics (Subtask 1.2)
  // --------------------------------------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return IG_BUSINESS_METRICS;
  },

  // --------------------------------------------------------------------------
  // getAvailableDimensions (Subtask 1.3)
  // --------------------------------------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return IG_BUSINESS_DIMENSIONS;
  },
};
